import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, Clock, Shield, Video, X, Loader2, MessageSquare, Phone, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';
import DeepScheduleModal from './DeepScheduleModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved - Schedule Now', color: 'bg-blue-100 text-blue-700' },
  scheduled: { label: 'Scheduled', color: 'bg-emerald-100 text-emerald-700' },
  active: { label: 'In Progress', color: 'bg-green-100 text-green-800 animate-pulse' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-600' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600' },
  rejected: { label: 'Not Available', color: 'bg-red-100 text-red-600' },
};

export default function AssistedOnboardingSessions({ onJoinSession }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleSession, setScheduleSession] = useState(null);

  const token = localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadSessions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/sessions`, { headers, cache: 'no-store' });
      if (res.ok) setSessions(await res.json());
    } catch (err) { logger.error('Failed to load sessions:', err); } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(); }, []);

  const handleCancel = async (sessionId) => {
    if (!window.confirm('Cancel this session?')) return;
    const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/sessions/${sessionId}/cancel`, { method: 'POST', headers });
    if (res.ok) { toast.success('Session cancelled'); loadSessions(); }
    else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch (err) { logger.error(err); return dateStr; }
  };

  if (loading) return <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...</div>;
  if (sessions.length === 0) return <div className="text-center py-6 text-slate-400"><MessageSquare className="w-7 h-7 mx-auto mb-2 opacity-50" /><p className="text-sm">No sessions yet</p></div>;

  return (
    <div className="space-y-3" data-testid="onboarding-sessions-list">
      {sessions.map(session => {
        const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.pending_review;
        const isQuick = session.session_type === 'quick_setup';
        const TypeIcon = isQuick ? Phone : GraduationCap;

        return (
          <Card key={session.session_id} className="border" data-testid={`session-${session.session_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${isQuick ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {isQuick ? 'Quick Virtual Setup Session' : 'Deep Onboarding'}
                    </Badge>
                    <Badge className={cfg.color}>{cfg.label}</Badge>
                    {session.masked_mode && (
                      <Badge variant="outline" className="text-xs"><Shield className="w-3 h-3 mr-1" /> Masked</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    {session.scheduled_date ? (
                      <>
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(session.scheduled_date)}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {session.scheduled_time} ({session.duration_minutes || (isQuick ? 15 : 60)} min)</span>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">Date: To be scheduled after approval</span>
                    )}
                  </div>
                  {session.qualification && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {session.qualification.help_topics?.slice(0, 3).map(t => (
                        <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                      ))}
                      {session.qualification.help_topics?.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{session.qualification.help_topics.length - 3} more</Badge>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {session.status === 'approved' && session.session_type === 'deep_onboarding' && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setScheduleSession(session)}
                      data-testid={`schedule-${session.session_id}`}>
                      <Calendar className="w-3 h-3 mr-1" /> Schedule Now
                    </Button>
                  )}
                  {(session.status === 'scheduled' || session.status === 'active') && (
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => onJoinSession?.(session)}
                      data-testid={`join-${session.session_id}`}>
                      <Video className="w-3 h-3 mr-1" /> Join
                    </Button>
                  )}
                  {['pending_review', 'approved', 'scheduled'].includes(session.status) && (
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                      onClick={() => handleCancel(session.session_id)}
                      data-testid={`cancel-${session.session_id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {scheduleSession && (
        <DeepScheduleModal
          isOpen={!!scheduleSession}
          onClose={() => setScheduleSession(null)}
          sessionId={scheduleSession.session_id}
          onSuccess={() => { toast.success('Session scheduled!'); loadSessions(); }}
        />
      )}
    </div>
  );
}
